import {
  InlineStack,
  Page,
  PageBody,
  PageHeader,
  PageHeaderIconTile,
  PageHeaderTabs,
  PageHeaderTitle,
  Stack,
  TabNav,
  TabNavList,
  TabNavTab,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { AutoRefreshControl } from '@/components/auto-refresh-control';
import { useAutoRefresh } from '@/features/user-flags';
import {
  availableDeliveryChannels,
  type DeliveryFilterValues,
  DeliveryFilters,
  DeliveryTable,
  ExportDeliveriesButton,
  useDeliveryActions,
} from '@/features/deliveries';
import { useAuth } from '@/services/auth';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { PlanFeature } from '@/services/auth/types';
import type { Delivery } from '@/services/db/delivery/entity';
import { useDeliveries } from '@/services/db/delivery/queries';
import { buildDeliveriesListParams } from '@/services/db/delivery/utils';
import { requireWorkspaceId } from '@/services/db/shared/workspace';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import {
  buildEmptyMessage,
  hasActiveFilters,
  toExportInput,
  toFilterInput,
  useOutboxFilters,
  useSeedDefaultDateRange,
} from './hooks';
import type { OutboxTab } from './types';

/** Auto-refresh polling interval — matches the Ember `<ScheduledTask @interval={{60000}}>`. */
const AUTO_REFRESH_INTERVAL_MS = 60_000;

type OutboxToolbarProps = {
  values: DeliveryFilterValues;
  onChange: (patch: Partial<DeliveryFilterValues>) => void;
  channels: readonly string[];
  isDrafts: boolean;
  workspaceId: number;
  canExport: boolean;
  lastUpdated: number;
  autoRefresh: boolean;
  onAutoRefreshChange: (next: boolean) => void;
};

/**
 * The filter bar plus the trailing toolbar actions (auto-refresh status + Export).
 * Drafts locks the status/user-action filters and caps the range at 30 days.
 */
function OutboxToolbar({
  values,
  onChange,
  channels,
  isDrafts,
  workspaceId,
  canExport,
  lastUpdated,
  autoRefresh,
  onAutoRefreshChange,
}: OutboxToolbarProps) {
  return (
    <DeliveryFilters
      values={values}
      onChange={onChange}
      channels={channels}
      drafted={isDrafts}
      isFilteringByEmailEnabled={!isDrafts}
      fixed={isDrafts ? 'deliveryStatus,userActions' : undefined}
      maxRange={isDrafts ? '30 days' : undefined}
      dateRangeTooltipMessage={isDrafts ? 'Drafts are stored only for 30 days' : undefined}
      actions={
        <InlineStack alignBlock="center" gap="200">
          <AutoRefreshControl
            lastUpdated={lastUpdated}
            autoRefresh={autoRefresh}
            onAutoRefreshChange={onAutoRefreshChange}
          />
          <ExportDeliveriesButton
            exportInput={{ ...toExportInput(values), drafted: isDrafts ? true : undefined }}
            from={isDrafts ? 'outbox_drafts' : 'outbox_deliveries'}
            workspaceId={workspaceId}
            isDisabled={!canExport}
          />
        </InlineStack>
      }
    />
  );
}

type OutboxPageProps = {
  /** Which tab is active — selects the drafts filter and the active tab. */
  tab: OutboxTab;
  /** Builds the href for each tab (the route owns the URL segment vocabulary). */
  tabHref: (tab: OutboxTab) => string;
  /** Builds a row's detail href (deliveries → React detail; drafts → Ember draft). */
  getDeliveryHref: (delivery: Delivery) => string;
};

/**
 * The Outbox page — the Deliveries/Sent (FPS-2591) and Drafts (FPS-2594) tabs,
 * both composed from the `features/deliveries` blocks. Ported from the Ember
 * `outbox/list` shell + `<cio-deliveries>`: one shared component parametrized by
 * tab. The route owns the URL segment vocabulary and passes the tab + the href
 * builders in.
 */
export default function OutboxPage({ tab, tabHref, getDeliveryHref }: OutboxPageProps) {
  const workspaceId = requireWorkspaceId();
  const { hasPermission } = usePermissions();
  // Match the Ember gates: export needs `journeys/exports/create`; the bulk send/retry/delete
  // actions need `journeys/messages/send` (its absence is the "read-only" state).
  const canExport = hasPermission('journeys/exports/create') === true;
  const canSendMessages = hasPermission('journeys/messages/send') === true;
  const taxonomy = useProductTaxonomy();
  const { values: rawValues, onChange } = useOutboxFilters();
  // Apply the last-30-days default window when no range is set (Ember base-deliveries parity),
  // so the picker shows the window and the first query is already bounded.
  const values = useSeedDefaultDateRange(rawValues);
  const { autoRefresh, onAutoRefreshChange } = useAutoRefresh();
  const auth = useAuth();
  const isLiveNotificationsAvailable =
    auth.account != null && hasUnlimitedPlanFeatureAccess(auth.account, PlanFeature.LiveNotifications);
  const channels = availableDeliveryChannels(isLiveNotificationsAvailable);

  const isDrafts = tab === 'drafts';
  const params = buildDeliveriesListParams({ ...toFilterInput(values), drafts: isDrafts ? true : undefined });
  const refetchInterval = autoRefresh ? AUTO_REFRESH_INTERVAL_MS : false;
  const { deliveries, isLoading, isError, refetch, hasMore, fetchMore, isFetchingMore, lastUpdated } = useDeliveries({
    params,
    queryConfig: { refetchInterval },
  });
  const actions = useDeliveryActions({ deliveries, deliveryStatus: values.deliveryStatus, workspaceId });

  return (
    <Page>
      <PageHeader>
        <PageHeaderIconTile name="delivery-log" />
        <PageHeaderTitle>{taxonomy.t('messageActivity.pageTitleWithFormerName')}</PageHeaderTitle>
        <PageHeaderTabs>
          <TabNav aria-label={taxonomy.t('messageActivity.pageTitle')}>
            <TabNavList>
              <TabNavTab href={tabHref('deliveries')} isActive={tab === 'deliveries'}>
                {taxonomy.t('nav.review.outbox.deliveries')}
              </TabNavTab>
              <TabNavTab href={tabHref('drafts')} isActive={tab === 'drafts'}>
                {taxonomy.t('nav.review.outbox.drafts')}
              </TabNavTab>
            </TabNavList>
          </TabNav>
        </PageHeaderTabs>
      </PageHeader>
      <PageBody>
        <Stack gap="300">
          <OutboxToolbar
            values={values}
            onChange={onChange}
            channels={channels}
            isDrafts={isDrafts}
            workspaceId={workspaceId}
            canExport={canExport}
            lastUpdated={lastUpdated}
            autoRefresh={autoRefresh}
            onAutoRefreshChange={onAutoRefreshChange}
          />
          <DeliveryTable
            deliveries={deliveries}
            isLoading={isLoading}
            isError={isError}
            onReload={() => void refetch()}
            hasMore={hasMore}
            onLoadMore={() => void fetchMore()}
            isLoadingMore={isFetchingMore}
            getDeliveryHref={getDeliveryHref}
            actionType={values.actionType}
            hasFilters={hasActiveFilters(values)}
            emptyMessage={buildEmptyMessage(
              values,
              isDrafts,
              taxonomy.t('messageActivity.deliveriesLearnMoreNoun').toLowerCase(),
            )}
            isDrafted={isDrafts}
            hasReadOnlyPermission={!canSendMessages}
            onBulkRetry={actions.onBulkRetry}
            onBulkSend={actions.onBulkSend}
            onBulkDelete={actions.onBulkDelete}
            onBulkRedeliver={actions.onBulkRedeliver}
            onRowRetry={actions.onRowRetry}
            onRowDelete={actions.onRowDelete}
          />
        </Stack>
      </PageBody>
    </Page>
  );
}
